import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  model,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { distinctUntilChanged, map, tap } from 'rxjs';

import { GovukSelectOption } from '@netz/govuk-components';

/**
 * Marked for refactor
 * Remove HMCTS specific scss, new govuk-frontend includes pagination, move to @netz/govuk-components
 */
@Component({
  selector: 'cca-pagination',
  templateUrl: './pagination.component.html',
  standalone: true,
  imports: [AsyncPipe, RouterLink, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnChanges {
  public readonly activatedRoute = inject(ActivatedRoute);

  readonly pageSize = model<number>();
  readonly count = input<number>();
  readonly hideNumbers = input(false);

  readonly currentPageChange = output<number>();
  readonly pageSizeChange = output<number>();

  totalPages = 1;
  pageNumbers = [1];

  readonly pageSizeNumbers: GovukSelectOption[] = [
    { text: '10', value: 10 },
    { text: '20', value: 20 },
    { text: '30', value: 30 },
    { text: '40', value: 40 },
    { text: '50', value: 50 },
  ];

  readonly pageSizeForm = new FormGroup({
    pageSize: new FormControl<number>(this.pageSize() || 10),
  });

  readonly currentPage = this.activatedRoute.queryParamMap.pipe(
    map((res) => Number(res.get('page') || 1)),
    distinctUntilChanged(),
    tap((page) => this.currentPageChange.emit(page)),
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.count || changes?.pageSize) {
      this.calculatePageCount();

      if (changes?.pageSize) {
        this.pageSizeForm.setValue({ pageSize: this.pageSize() || 10 });
      }
    }
  }

  isDisplayed(target: number, current: number): boolean {
    return this.pageNumbers.length <= 6 || (target >= current - 1 && target <= current + 1);
  }

  isDots(target: number, current: number): boolean {
    return (
      this.pageNumbers.length > 6 &&
      target !== 1 &&
      target !== this.totalPages &&
      (target === current - 2 || target === current + 2)
    );
  }

  onPageSizeChange() {
    this.pageSize.set(this.pageSizeForm.value.pageSize);
    this.pageSizeChange.emit(this.pageSize());
    this.calculatePageCount();
  }

  getStartingItem(currentPage: number): number {
    if (this.count() === 0) return 0;
    return (currentPage - 1) * this.pageSize() + 1;
  }

  getEndingItem(currentPage: number): number {
    if (this.count() === 0) return 0;
    const endingItem = currentPage * this.pageSize();
    return endingItem > this.count() ? this.count() : endingItem;
  }

  private calculatePageCount(): void {
    this.totalPages = Math.ceil((this.count() || 1) / (this.pageSize() || 1));
    this.pageNumbers = Array(this.totalPages)
      .fill(0)
      .map((_, i) => i + 1);
  }
}
